import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronRight,
  faEllipsisVertical,
  faFolder,
  faFolderOpen,
  faFolderPlus,
} from "@fortawesome/free-solid-svg-icons";
import { Menu, MenuItem } from "@mui/material";
import { DesignFolder } from "../../../core/models/entities/DesignFolder";
import { PrimaryButton } from "../Buttons/PrimaryButton";
import { useConfirmationDialog } from "../Dialogs/confirmation-dialog";
import { useErrorToast } from "../Toast/Toast";

// Drag payload contracts between the list rows (drag sources) and the folder
// cards (drop targets): a JSON array of ids. The design and logo lists use
// separate MIME types so a row dragged from one list can never be dropped as
// the other kind.
export const DESIGN_DRAG_MIME = "application/x-kitmaker-design-ids";
export const LOGO_DRAG_MIME = "application/x-kitmaker-logo-ids";
// Dragging a folder CARD (payload: a single JSON id). Separate from the item
// MIMEs above so a folder drop is never mistaken for a design/logo drop.
export const FOLDER_DRAG_MIME = "application/x-kitmaker-folder-id";

export function readDraggedIds(
  dataTransfer: DataTransfer,
  mime: string,
): number[] {
  try {
    const raw = dataTransfer.getData(mime);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x) => Number.isFinite(x)) : [];
  } catch {
    return [];
  }
}

export function readDraggedFolderId(dataTransfer: DataTransfer): number | null {
  try {
    const raw = dataTransfer.getData(FOLDER_DRAG_MIME);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

// Pseudo-folder shown at root only: items without a folder. Navigating to it
// applies filter value '0'; dropping items on it unfiles them (folderId null).
export const UNFILED_FOLDER_ID = 0;

// Flat folder list for a "Move to folder" picker, children indented under
// their parent (depth-first).
export function flattenFolders(
  all: DesignFolder[],
  parentId: number | null = null,
  depth: number = 0,
): { folder: DesignFolder; depth: number }[] {
  return all
    .filter((f) => (f.parentId ?? null) === parentId)
    .flatMap((f) => [
      { folder: f, depth },
      ...flattenFolders(all, f.id, depth + 1),
    ]);
}

// Ids of every folder BELOW rootId in the tree, computed from the flat list.
// Used to keep a folder from being moved into itself/its own subtree.
export function collectDescendantIds(
  all: DesignFolder[],
  rootId: number,
): Set<number> {
  const ids = new Set<number>();
  let frontier = [rootId];
  while (frontier.length > 0) {
    const next = all.filter(
      (f) =>
        f.parentId !== null && frontier.includes(f.parentId) && !ids.has(f.id),
    );
    next.forEach((f) => ids.add(f.id));
    frontier = next.map((f) => f.id);
  }
  return ids;
}

interface FolderBarProps {
  folders: DesignFolder[];
  // null = root ("All designs"/"All logos"), UNFILED_FOLDER_ID = the Unfiled
  // pseudo-folder
  currentFolderId: number | null;
  // Root breadcrumb label: "All designs" on the design list, "All logos" on
  // the logo list (the ONE folder tree per org is shared between them).
  rootLabel: string;
  // Which per-folder count to show: the design list shows designCount, the
  // logo list logoCount.
  getFolderCount: (folder: DesignFolder) => number;
  // Drag payload MIME the folder cards accept (DESIGN_DRAG_MIME on the design
  // list, LOGO_DRAG_MIME on the logo list).
  dragMime: string;
  onNavigate: (folderId: number | null) => void;
  onCreateFolder: (name: string) => Promise<void>;
  onRenameFolder: (folder: DesignFolder, newName: string) => Promise<void>;
  onDeleteFolder: (folder: DesignFolder) => Promise<void>;
  // Re-parent a folder (parentId null = top level). The bar guards against
  // self/descendant/unchanged-parent targets BEFORE calling this.
  onMoveFolder: (folder: DesignFolder, parentId: number | null) => Promise<void>;
  // Persist a new sibling order for ONE level (parentId null = top level):
  // orderedIds is the full id order of that level. Called after a drop on a
  // card's left/right reorder zone; the bar renders the new order
  // optimistically while the refetch confirms.
  onReorderFolders: (
    parentId: number | null,
    orderedIds: number[],
  ) => Promise<void>;
  // folder null = unfile (drop on the Unfiled pseudo-card)
  onDropItems: (folder: DesignFolder | null, ids: number[]) => void;
  // Count for the Unfiled pseudo-card (undefined hides the count)
  unfiledCount?: number;
}

export function FolderBar({
  folders,
  currentFolderId,
  rootLabel,
  getFolderCount,
  dragMime,
  onNavigate,
  onCreateFolder,
  onRenameFolder,
  onDeleteFolder,
  onMoveFolder,
  onReorderFolders,
  onDropItems,
  unfiledCount,
}: FolderBarProps) {
  const [creating, setCreating] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  // Optimistic id order of the CURRENT level while a reorder request is in
  // flight (null = render the server order).
  const [pendingOrder, setPendingOrder] = useState<number[] | null>(null);

  const isUnfiled = currentFolderId === UNFILED_FOLDER_ID;
  const currentFolder =
    currentFolderId !== null && !isUnfiled
      ? folders.find((f) => f.id === currentFolderId)
      : undefined;

  // Breadcrumb: walk parentId up from the current folder (root label first).
  const path: DesignFolder[] = [];
  let cursor = currentFolder;
  while (cursor) {
    path.unshift(cursor);
    cursor =
      cursor.parentId !== null
        ? folders.find((f) => f.id === cursor!.parentId)
        : undefined;
    if (path.length > 20) break; // guard against a malformed parent cycle
  }

  // Folder cards for the CURRENT level: root shows top-level folders (plus the
  // Unfiled pseudo-card); inside a folder its children are shown; the Unfiled
  // pseudo-folder has no children.
  const levelParentId = currentFolder ? currentFolder.id : null;
  const levelFolders = isUnfiled
    ? []
    : folders.filter((f) => (f.parentId ?? null) === levelParentId);

  // Cards render in server order (sortOrder per sibling level); while a
  // reorder drop is in flight the optimistic pendingOrder wins.
  const sortedLevelFolders = [...levelFolders].sort((a, b) =>
    pendingOrder
      ? pendingOrder.indexOf(a.id) - pendingOrder.indexOf(b.id)
      : a.sortOrder - b.sortOrder,
  );

  // Shared entry point for every folder move (card drop, breadcrumb drop and
  // the "Move to..." picker): guards run here, once, before the API is hit.
  async function requestMoveFolder(
    folder: DesignFolder,
    parentId: number | null,
  ) {
    if ((folder.parentId ?? null) === parentId) return; // already there
    if (
      folder.id === parentId ||
      (parentId !== null && collectDescendantIds(folders, folder.id).has(parentId))
    ) {
      useErrorToast("A folder can't be moved into its own subfolder");
      return;
    }
    await onMoveFolder(folder, parentId);
  }

  function handleFolderDrop(parentId: number | null, draggedFolderId: number) {
    const dragged = folders.find((f) => f.id === draggedFolderId);
    if (dragged) requestMoveFolder(dragged, parentId).then();
  }

  // A folder card was dropped on the LEFT/RIGHT reorder zone of a card on the
  // current level: insert it before/after that card. A folder dragged in from
  // a DIFFERENT level is re-parented to this level first (same subtree guard
  // as a move drop), then inserted.
  async function handleReorderDrop(
    target: DesignFolder,
    side: "before" | "after",
    draggedFolderId: number,
  ) {
    const dragged = folders.find((f) => f.id === draggedFolderId);
    if (!dragged || dragged.id === target.id) return;
    if ((dragged.parentId ?? null) !== levelParentId) {
      if (
        dragged.id === levelParentId ||
        (levelParentId !== null &&
          collectDescendantIds(folders, dragged.id).has(levelParentId))
      ) {
        useErrorToast("A folder can't be moved into its own subfolder");
        return;
      }
      await onMoveFolder(dragged, levelParentId);
    }
    const orderedIds = sortedLevelFolders
      .map((f) => f.id)
      .filter((id) => id !== dragged.id);
    const targetIndex = orderedIds.indexOf(target.id);
    if (targetIndex === -1) return;
    orderedIds.splice(
      side === "before" ? targetIndex : targetIndex + 1,
      0,
      dragged.id,
    );
    setPendingOrder(orderedIds); // optimistic: render the new order right away
    try {
      await onReorderFolders(levelParentId, orderedIds);
    } finally {
      // The refetch (awaited inside onReorderFolders) delivered fresh
      // sortOrders - or the call failed; either way the server order rules.
      setPendingOrder(null);
    }
  }

  // Flat picker options for a card's "Move to..." (same pattern as the lists'
  // move-selected picker); self + descendants are cut per folder below.
  const flatFolders = flattenFolders(folders);

  async function handleCreate() {
    const name = newFolderName.trim();
    if (!name || isSaving) return;
    setIsSaving(true);
    try {
      await onCreateFolder(name);
      setNewFolderName("");
      setCreating(false);
    } catch {
      // Parent showed a toast; keep the input open so the user can retry.
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="mb-3 space-y-2">
      {/* Breadcrumb row. Every crumb is also a drop target: items land IN that
          folder (root = unfile), a dragged folder card is re-parented TO that
          level (root = top level). */}
      <div className="flex items-center flex-wrap gap-1 text-sm">
        <CrumbDropZone
          dragMime={dragMime}
          onDropItemIds={(ids) => onDropItems(null, ids)}
          onDropFolder={(draggedId) => handleFolderDrop(null, draggedId)}
        >
          <button
            className={`hover:underline ${
              currentFolderId === null ? "font-bold" : "text-gray-600"
            }`}
            onClick={() => onNavigate(null)}
          >
            {rootLabel}
          </button>
        </CrumbDropZone>
        {isUnfiled && (
          <>
            <FontAwesomeIcon
              icon={faChevronRight}
              className="text-gray-400 text-xs"
            />
            <span className="font-bold">Unfiled</span>
          </>
        )}
        {path.map((folder, i) => (
          <React.Fragment key={folder.id}>
            <FontAwesomeIcon
              icon={faChevronRight}
              className="text-gray-400 text-xs"
            />
            <CrumbDropZone
              dragMime={dragMime}
              onDropItemIds={(ids) => onDropItems(folder, ids)}
              onDropFolder={(draggedId) => handleFolderDrop(folder.id, draggedId)}
            >
              {i === path.length - 1 ? (
                <span className="font-bold">{folder.name}</span>
              ) : (
                <button
                  className="text-gray-600 hover:underline"
                  onClick={() => onNavigate(folder.id)}
                >
                  {folder.name}
                </button>
              )}
            </CrumbDropZone>
          </React.Fragment>
        ))}
      </div>

      {/* Folder cards for the current level */}
      <div className="flex flex-wrap gap-2 items-stretch">
        {currentFolderId === null && (
          <FolderCard
            name="Unfiled"
            count={unfiledCount}
            icon={faFolderOpen}
            dragMime={dragMime}
            onOpen={() => onNavigate(UNFILED_FOLDER_ID)}
            onDropItems={(ids) => onDropItems(null, ids)}
          />
        )}
        {sortedLevelFolders.map((folder) => {
          const blocked = collectDescendantIds(folders, folder.id);
          return (
            <FolderCard
              key={folder.id}
              name={folder.name}
              count={getFolderCount(folder)}
              icon={faFolder}
              dragMime={dragMime}
              folderId={folder.id}
              onOpen={() => onNavigate(folder.id)}
              onDropItems={(ids) => onDropItems(folder, ids)}
              onDropFolder={(draggedId) => handleFolderDrop(folder.id, draggedId)}
              onReorderDrop={(side, draggedId) =>
                handleReorderDrop(folder, side, draggedId).then()
              }
              onRename={(name) => onRenameFolder(folder, name)}
              onDelete={() => onDeleteFolder(folder)}
              moveTargets={flatFolders.filter(
                ({ folder: f }) => f.id !== folder.id && !blocked.has(f.id),
              )}
              onMoveTo={(parentId) => requestMoveFolder(folder, parentId).then()}
            />
          );
        })}
        {!isUnfiled &&
          (creating ? (
            <div className="flex items-center gap-2 border rounded-md px-3 py-2 bg-white">
              <FontAwesomeIcon icon={faFolder} className="text-gray-400" />
              <input
                autoFocus
                className="border rounded px-2 py-1 text-sm w-40"
                placeholder="Folder name"
                value={newFolderName}
                disabled={isSaving}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleCreate();
                  if (e.key === "Escape") {
                    setCreating(false);
                    setNewFolderName("");
                  }
                }}
              />
              <PrimaryButton
                width="xs"
                className="px-3"
                disabled={!newFolderName.trim() || isSaving}
                onClick={handleCreate}
              >
                {isSaving ? "Creating..." : "Create"}
              </PrimaryButton>
              <button
                className="text-xs text-gray-500 hover:underline"
                onClick={() => {
                  setCreating(false);
                  setNewFolderName("");
                }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              className="flex items-center gap-2 border border-dashed rounded-md px-3 py-2 text-sm text-gray-600 hover:bg-gray-100"
              onClick={() => setCreating(true)}
            >
              <FontAwesomeIcon icon={faFolderPlus} />
              New folder
            </button>
          ))}
      </div>
    </div>
  );
}

// Breadcrumb crumbs double as drop targets (folder MIME first - an item drag
// never carries it, so item drops behave exactly as on the cards).
function CrumbDropZone({
  dragMime,
  onDropItemIds,
  onDropFolder,
  children,
}: {
  dragMime: string;
  onDropItemIds: (ids: number[]) => void;
  onDropFolder: (draggedFolderId: number) => void;
  children: React.ReactNode;
}) {
  const [dragOver, setDragOver] = useState(false);

  function acceptsDrag(e: React.DragEvent) {
    return (
      e.dataTransfer.types.includes(dragMime) ||
      e.dataTransfer.types.includes(FOLDER_DRAG_MIME)
    );
  }

  return (
    <span
      className={`rounded px-1 -mx-1 ${
        dragOver ? "ring-2 ring-success bg-gray-100" : ""
      }`}
      onDragOver={(e) => {
        if (!acceptsDrag(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        setDragOver(false);
        if (!acceptsDrag(e)) return;
        e.preventDefault();
        if (e.dataTransfer.types.includes(FOLDER_DRAG_MIME)) {
          const draggedId = readDraggedFolderId(e.dataTransfer);
          if (draggedId !== null) onDropFolder(draggedId);
          return;
        }
        const ids = readDraggedIds(e.dataTransfer, dragMime);
        if (ids.length > 0) onDropItemIds(ids);
      }}
    >
      {children}
    </span>
  );
}

interface FolderCardProps {
  name: string;
  count?: number;
  icon: typeof faFolder;
  dragMime: string;
  // Real folders only (the Unfiled pseudo-card has no id): makes the card
  // itself draggable as a FOLDER_DRAG_MIME payload.
  folderId?: number;
  onOpen: () => void;
  onDropItems: (ids: number[]) => void;
  // Another folder card was dropped on this card (absent on the Unfiled
  // pseudo-card - folders cannot be "unfiled", only moved to top level).
  onDropFolder?: (draggedFolderId: number) => void;
  // Another folder card was dropped on this card's LEFT/RIGHT reorder zone
  // (outer 25% each side): insert the dragged folder before/after this card.
  // Absent on the Unfiled pseudo-card - it is pinned first and not sortable.
  onReorderDrop?: (side: "before" | "after", draggedFolderId: number) => void;
  onRename?: (newName: string) => Promise<void>;
  onDelete?: () => Promise<void>;
  // "Move to..." picker options (self + descendants already excluded)
  moveTargets?: { folder: DesignFolder; depth: number }[];
  onMoveTo?: (parentId: number | null) => void;
}

function FolderCard({
  name,
  count,
  icon,
  dragMime,
  folderId,
  onOpen,
  onDropItems,
  onDropFolder,
  onReorderDrop,
  onRename,
  onDelete,
  moveTargets,
  onMoveTo,
}: FolderCardProps) {
  const [dragOver, setDragOver] = useState(false);
  // Which reorder zone a FOLDER drag is hovering (renders the insert line);
  // null = middle zone or an item drag (renders the existing ring).
  const [insertSide, setInsertSide] = useState<"before" | "after" | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [moveMenuAnchor, setMoveMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(name);
  const [isSaving, setIsSaving] = useState(false);
  const deleteConfirmation = useConfirmationDialog(
    "Do you want to delete this folder?",
    () => {
      if (onDelete) onDelete();
    },
  );

  function acceptsDrag(e: React.DragEvent) {
    return (
      e.dataTransfer.types.includes(dragMime) ||
      (!!onDropFolder && e.dataTransfer.types.includes(FOLDER_DRAG_MIME))
    );
  }

  async function handleRename() {
    const newName = renameValue.trim();
    if (!newName || !onRename || isSaving) return;
    setIsSaving(true);
    try {
      await onRename(newName);
      setRenaming(false);
    } catch {
      // Parent showed a toast; keep the input open so the user can retry.
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div
      className={`relative flex items-center gap-2 border rounded-md px-3 py-2 bg-white cursor-pointer select-none hover:bg-gray-100 transition-colors ${
        dragOver ? "ring-2 ring-success bg-gray-100" : ""
      }`}
      onClick={() => {
        if (!renaming) onOpen();
      }}
      draggable={folderId !== undefined && !renaming}
      onDragStart={(e) => {
        if (folderId === undefined || renaming) return;
        e.dataTransfer.setData(FOLDER_DRAG_MIME, JSON.stringify(folderId));
        e.dataTransfer.setData("text/plain", name);
        e.dataTransfer.effectAllowed = "move";
      }}
      onDragOver={(e) => {
        if (!acceptsDrag(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        // A FOLDER drag splits the card into zones: outer LEFT/RIGHT 25% =
        // reorder (insert line), middle 50% = move inside (existing ring).
        // Item drags never carry the folder MIME and take the ring as before.
        if (onReorderDrop && e.dataTransfer.types.includes(FOLDER_DRAG_MIME)) {
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const side =
            x < rect.width * 0.25
              ? ("before" as const)
              : x > rect.width * 0.75
                ? ("after" as const)
                : null;
          setInsertSide(side);
          setDragOver(side === null);
          return;
        }
        setDragOver(true);
      }}
      onDragLeave={() => {
        setDragOver(false);
        setInsertSide(null);
      }}
      onDrop={(e) => {
        const side = insertSide;
        setDragOver(false);
        setInsertSide(null);
        if (!acceptsDrag(e)) return;
        e.preventDefault();
        // Folder MIME first: an item drag never carries it, so the existing
        // design/logo drop path below is untouched.
        if (onDropFolder && e.dataTransfer.types.includes(FOLDER_DRAG_MIME)) {
          const draggedId = readDraggedFolderId(e.dataTransfer);
          if (draggedId === null) return;
          if (side && onReorderDrop) onReorderDrop(side, draggedId);
          else onDropFolder(draggedId);
          return;
        }
        const ids = readDraggedIds(e.dataTransfer, dragMime);
        if (ids.length > 0) onDropItems(ids);
      }}
    >
      {/* 2px insert indicator, centered in the 8px gap next to the card */}
      {insertSide && (
        <span
          className={`pointer-events-none absolute inset-y-0 w-0.5 rounded bg-success ${
            insertSide === "before" ? "-left-[5px]" : "-right-[5px]"
          }`}
        />
      )}
      <FontAwesomeIcon icon={icon} className="text-gray-500" />
      {renaming ? (
        <input
          autoFocus
          className="border rounded px-2 py-1 text-sm w-36"
          value={renameValue}
          disabled={isSaving}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => setRenameValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleRename();
            if (e.key === "Escape") setRenaming(false);
          }}
        />
      ) : (
        <span className="text-sm font-medium">{name}</span>
      )}
      {count !== undefined && (
        <span className="text-xs text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">
          {count}
        </span>
      )}
      {(onRename || onDelete) && (
        <>
          <button
            className="text-gray-400 hover:text-gray-700 px-1"
            onClick={(e) => {
              e.stopPropagation();
              setMenuAnchor(e.currentTarget);
            }}
          >
            <FontAwesomeIcon icon={faEllipsisVertical} />
          </button>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            onClick={(e) => e.stopPropagation()}
          >
            {onRename && (
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  setRenameValue(name);
                  setRenaming(true);
                }}
              >
                Rename
              </MenuItem>
            )}
            {moveTargets && onMoveTo && (
              <MenuItem
                onClick={() => {
                  // Swap to the picker on the same anchor (menuAnchor still
                  // holds the three-dot button until this handler returns).
                  setMoveMenuAnchor(menuAnchor);
                  setMenuAnchor(null);
                }}
              >
                Move to...
              </MenuItem>
            )}
            {onDelete && (
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  deleteConfirmation.open();
                }}
              >
                Delete
              </MenuItem>
            )}
          </Menu>
          {moveTargets && onMoveTo && (
            // Same flat picker pattern as the lists' "Move selected" menu:
            // children indented under their parent, root option on top.
            <Menu
              anchorEl={moveMenuAnchor}
              open={Boolean(moveMenuAnchor)}
              onClose={() => setMoveMenuAnchor(null)}
              onClick={(e) => e.stopPropagation()}
            >
              <MenuItem disabled>Move to folder</MenuItem>
              <MenuItem
                onClick={() => {
                  setMoveMenuAnchor(null);
                  onMoveTo(null);
                }}
              >
                <FontAwesomeIcon icon={faFolder} className="mr-2 w-4" />
                Top level
              </MenuItem>
              {moveTargets.map(({ folder, depth }) => (
                <MenuItem
                  key={folder.id}
                  sx={{ paddingLeft: `${16 + depth * 16}px` }}
                  onClick={() => {
                    setMoveMenuAnchor(null);
                    onMoveTo(folder.id);
                  }}
                >
                  <FontAwesomeIcon icon={faFolder} className="mr-2 w-4" />
                  {folder.name}
                </MenuItem>
              ))}
            </Menu>
          )}
          {/* MUI portals still bubble React events through the tree: keep
              dialog clicks from triggering the card's onOpen. */}
          <div onClick={(e) => e.stopPropagation()}>
            {deleteConfirmation.component}
          </div>
        </>
      )}
    </div>
  );
}
